import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { Group as ResizableGroup, Panel as ResizablePanel } from 'react-resizable-panels'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { NavigationItem } from '@unified-lk/nav'
import type { AuthSession, StaffPlanSummary } from '../../types/plans'
import { StaffShell } from '../StaffShell'
import { staffApi } from '../../api/staff'
import { EventTypes, logEvent } from '@unified-lk/event-log'

import {
  comparePlansByCreatedAtDesc,
  getStageSortOrder,
  getServices,
  getServiceQty,
  normalizeDoctorName,
} from './constructor/constructorTypes'
import type {
  ConstructorPlan,
  ConstructorPlansResponse,
  ConstructorPlanDetailResponse,
  MisServiceItem,
  MisDragItem,
  DoctorOption,
} from './constructor/constructorTypes'
import { VResizeHandle, HResizeHandle } from './constructor/ConstructorResizeHandles'
import { DragOverlayCard } from './constructor/MisDragSource'
import { ConstructorPlanListRail } from './constructor/ConstructorPlanListRail'
import { MisPlansSidebar } from './constructor/MisPlansSidebar'
import { MisPlanCorrectionShell } from './constructor/MisPlanCorrectionShell'
import { ConstructorPlanHeader } from './constructor/ConstructorPlanHeader'
import { ConstructorStagesEditor } from './constructor/ConstructorStagesEditor'
import { ConstructorRightDrawer } from './constructor/ConstructorRightDrawer'
import { ConstructorWorkflowProvider } from './constructor/shell/ConstructorWorkflowContext'
import { ConstructorDiagnosticsDraftProvider } from './constructor/shell/ConstructorDiagnosticsDraftContext'
import { ConstructorWorkflowWindows } from './constructor/shell/ConstructorWorkflowWindows'
import { useConstructorWorkflow } from './constructor/shell/ConstructorWorkflowContext'
import { ConstructorPrintView, constructorPrintStyle } from './constructor/ConstructorPrintView'
import { CreatePlanModal } from './constructor/CreatePlanModal'
import {
  misImportedServicesFromPlan,
  misImportedServicesToMisServiceItems,
  type MisImportedPlanSummary,
} from './constructor/auto-distribute/selectedPlanServices'
import {
  parseInitialPlanIdFromUrl,
  readConstructorPlansCache,
  writeConstructorPlansCache,
} from './constructor/constructorPlansCache'
import { filterVisibleConstructorPlans } from './constructor/constructorPlanVisibility'

const PLAN_LOAD_TIMEOUT_MS = 20_000
const MIS_SUMMARIES_TIMEOUT_MS = 8_000


function formatPlanLoadError(raw: string, planId: number): string {
  if (raw === 'plan_not_found') {
    return `План #${planId} не найден или недоступен для вашего аккаунта.`
  }
  if (raw === 'unauthorized') {
    return 'Сессия истекла — обновите страницу и войдите снова.'
  }
  return raw
}



function CloseWorkflowWindowsOnPlanChange({ planId }: { planId: number | null }) {
  const { closeWindow } = useConstructorWorkflow()
  const prevPlanIdRef = useRef<number | null>(null)
  useEffect(() => {
    if (prevPlanIdRef.current !== null && prevPlanIdRef.current !== planId) {
      closeWindow('plan-presentation')
      closeWindow('plan-review')
    }
    prevPlanIdRef.current = planId
  }, [planId, closeWindow])
  return null
}


export function StaffConstructorWorkspace({
  bootstrap,
  session,
  navigation,
  onNavigate,
  onLogout,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  navigation: NavigationItem[]
  onNavigate: (view: string) => void
  onLogout: () => void
}) {
  const initialPlanIdFromUrl = parseInitialPlanIdFromUrl()
  const cachedPlans = readConstructorPlansCache(bootstrap.tenant)

  // ---- Plan list state ----
  const [plans, setPlans] = useState<ConstructorPlan[]>(() => cachedPlans ?? [])
  const [loading, setLoading] = useState(() => cachedPlans === null)
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(() => initialPlanIdFromUrl)
  const apiBase = bootstrap.apiBase
  const accessToken = session.accessToken
  const tenantId = bootstrap.tenant
  const [planDetail, setPlanDetail] = useState<ConstructorPlan | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [planLoadError, setPlanLoadError] = useState<string | null>(null)
  const planLoadAbortRef = useRef<AbortController | null>(null)
  const planDetailFetchRef = useRef<number | null>(null)
  const plansLoadedRef = useRef(false)
  const misSummariesPatientRef = useRef<number | null>(null)
  const loadPlanDetailRef = useRef<((planId: number) => Promise<void>) | null>(null)
  const planLoadSeqRef = useRef(0)
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [stageOrder, setStageOrder] = useState<number[]>([])

  // ---- MIS sidebar state ----
  const [misImportedSummaries, setMisImportedSummaries] = useState<MisImportedPlanSummary[]>([])
  const [misPatientId, setMisPatientId] = useState<string | null>(null)
  const [misLoading, setMisLoading] = useState(false)
  const [expandedMisPlanIds, setExpandedMisPlanIds] = useState<string[]>([])
  const [misServicesCache, setMisServicesCache] = useState<Record<string, MisServiceItem[]>>({})
  const [misServicesLoadingIds, setMisServicesLoadingIds] = useState<string[]>([])
  const [misSearch, setMisSearch] = useState<string>('')

  // ---- Layout & interaction state ----
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true)
  const [doctors, setDoctors] = useState<DoctorOption[]>([])
  const [selectedMisIds, setSelectedMisIds] = useState<Set<string>>(new Set())
  const [dragPreview, setDragPreview] = useState<MisDragItem[]>([])
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [misPlanCorrectionTarget, setMisPlanCorrectionTarget] = useState<MisImportedPlanSummary | null>(null)

  const stageSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  // Keys of MIS services already dropped into the active plan — used to highlight them yellow in the sidebar.
  const usedMisServiceKeys = useMemo<Set<string>>(() => {
    if (!planDetail) return new Set()
    const keys = new Set<string>()
    for (const stage of planDetail.stages ?? []) {
      for (const svc of stage.services ?? []) {
        const row = svc as unknown as Record<string, unknown>
        const misPlanId = row.mis_source_plan_id ?? row.mis_source_plan_uuid
        if (misPlanId != null && String(misPlanId).trim() !== '') {
          keys.add(`${misPlanId}:${svc.service_name ?? ''}:${svc.tooth_number ?? ''}`)
        }
      }
    }
    return keys
  }, [planDetail])

  const showToast = useCallback((kind: 'success' | 'error', text: string) => {
    setToast({ kind, text })
    setTimeout(() => setToast(null), 3500)
  }, [])

  const toggleMisSelected = useCallback((id: string) => {
    setSelectedMisIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const clearMisSelection = useCallback(() => {
    setSelectedMisIds(new Set())
  }, [])

  const allSelectedItems = useMemo<MisDragItem[]>(() => {
    if (selectedMisIds.size === 0) return []
    const items: MisDragItem[] = []
    for (const planId in misServicesCache) {
      const list = misServicesCache[planId]
      if (!list) continue
      for (const svc of list) {
        if (selectedMisIds.has(svc.id)) {
          items.push({
            planId: null,
            misPlanUuid: svc.mis_plan_uuid ?? planId,
            service: svc,
          })
        }
      }
    }
    return items
  }, [selectedMisIds, misServicesCache])

  // ---- Data loaders ----

  const loadPlans = useCallback(async (opts: { background?: boolean; patientId?: number } = {}) => {
    if (!opts.background) setLoading(true)
    try {
      const res = await staffApi.getConstructorPlans(bootstrap, session, {
        patientId: opts.patientId,
      }) as ConstructorPlansResponse
      const next = filterVisibleConstructorPlans((res.plans ?? []).slice()).sort(comparePlansByCreatedAtDesc)
      setPlans(next)
      writeConstructorPlansCache(tenantId, next)
    } catch {
      if (!opts.background) setPlans([])
    } finally {
      setLoading(false)
    }
  }, [apiBase, accessToken, tenantId, bootstrap, session])


  const loadMisImportedSummaries = useCallback(async (patientId: number | null, expandPlanId?: string | null) => {
    if (!patientId || patientId <= 0) {
      setMisImportedSummaries([])
      setMisPatientId(null)
      setExpandedMisPlanIds([])
      setMisServicesCache({})
      setMisServicesLoadingIds([])
      return
    }
    setMisLoading(true)
    const misAbort = new AbortController()
    const misTimeoutId = window.setTimeout(() => misAbort.abort(), MIS_SUMMARIES_TIMEOUT_MS)
    try {
      const res = await fetch(`${apiBase}/staff/mis/treatment-plan-summaries?patient_id=${patientId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: misAbort.signal,
      })
      if (!res.ok) {
        setMisImportedSummaries([])
        setMisPatientId(null)
        if (res.status === 502 || res.status === 503) {
          showToast('error', 'МИС временно недоступен — подождите ~30 сек и нажмите «Обновить» в блоке планов МИС.')
        }
        return
      }
      const data = (await res.json()) as { items?: MisImportedPlanSummary[]; mis_patient_id?: string | null }
      const items = (data.items ?? []).slice().sort((a, b) => {
        const da = a.plan_date ?? a.created_at ?? ''
        const db = b.plan_date ?? b.created_at ?? ''
        return new Date(db).getTime() - new Date(da).getTime()
      })
      setMisImportedSummaries(items)
      setMisPatientId(data.mis_patient_id ?? null)
      setExpandedMisPlanIds((prev) => {
        if (expandPlanId) {
          return prev.includes(expandPlanId) ? prev : [expandPlanId, ...prev]
        }
        if (prev.length > 0) return prev
        return []
      })
    } catch {
      setMisImportedSummaries([])
      setMisPatientId(null)
    } finally {
      window.clearTimeout(misTimeoutId)
      setMisLoading(false)
    }
  }, [apiBase, accessToken, showToast])

  const loadPlanDetail = useCallback(async (planId: number) => {
    planLoadAbortRef.current?.abort()
    const controller = new AbortController()
    planLoadAbortRef.current = controller
    const seq = ++planLoadSeqRef.current

    setPlanLoadError(null)
    const cachedStub = plans.find((p) => p.id === planId)
      ?? readConstructorPlansCache(tenantId)?.find((p) => p.id === planId)
      ?? null
    if (cachedStub) {
      setPlanDetail(cachedStub)
    }
    setLoadingDetail(!cachedStub)
    const timeoutId = window.setTimeout(() => controller.abort(), PLAN_LOAD_TIMEOUT_MS)
    try {
      type LegacyPublicField = {
        token?: string; active?: boolean; expires_at?: string
        url?: string; hide_prices?: boolean; payment?: unknown
      }
      type DetailWithLegacyPublic = ConstructorPlanDetailResponse & {
        plan?: ConstructorPlan & { public?: LegacyPublicField }
      }
      const res = await fetch(`${apiBase}/staff/constructor/${planId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        signal: controller.signal,
      }).then(async (response) => {
        const json = await response.json().catch(() => ({}))
        if (!response.ok) {
          const message =
            typeof json === 'object' && json !== null
              ? String((json as Record<string, unknown>).message ?? (json as Record<string, unknown>).error ?? 'API request failed')
              : 'API request failed'
          throw new Error(message)
        }
        return json as DetailWithLegacyPublic
      })
      if (seq !== planLoadSeqRef.current) return

      const plan = res.plan ?? res.data?.plan ?? null
      if (plan) {
        if (res.data?.share) {
          plan.share = res.data.share
        } else {
          const legacyPub = (plan as ConstructorPlan & { public?: LegacyPublicField }).public
          if (legacyPub) {
            plan.share = {
              is_active: legacyPub.active ?? false,
              token: legacyPub.token,
              url: legacyPub.url,
              expires_at: legacyPub.expires_at,
              hide_prices: legacyPub.hide_prices ?? false,
              payment: legacyPub.payment ?? undefined,
            }
          }
        }
        setPlanDetail(plan)
        setPlanLoadError(null)
        const misSyncError = (plan as ConstructorPlan & { mis_sync_error?: string }).mis_sync_error
        if (misSyncError) {
          showToast('error', misSyncError)
        }
        const stages = (plan.stages ?? []).slice().sort((a, b) => getStageSortOrder(a) - getStageSortOrder(b))
        setStageOrder(stages.map(s => s.id))
      } else {
        setPlanDetail(null)
        const msg = `План #${planId} не найден`
        setPlanLoadError(msg)
        showToast('error', msg)
      }
    } catch (err) {
      if (seq !== planLoadSeqRef.current) return
      console.error('[loadPlanDetail] failed for plan', planId, err)
      setPlanDetail(null)
      const raw =
        err instanceof Error && err.name === 'AbortError'
          ? 'Таймаут загрузки плана (45 с). Повторите или обновите страницу.'
          : err instanceof Error
            ? err.message
            : 'Неизвестная ошибка'
      const msg = formatPlanLoadError(raw, planId)
      setPlanLoadError(msg)
      showToast('error', `Не удалось загрузить план: ${msg}`)
    } finally {
      window.clearTimeout(timeoutId)
      if (seq === planLoadSeqRef.current) {
        setLoadingDetail(false)
      }
    }
  }, [apiBase, accessToken, showToast])

  loadPlanDetailRef.current = loadPlanDetail

  // ---- Effects ----

  useEffect(() => {
    if (plansLoadedRef.current) return
    plansLoadedRef.current = true
    void loadPlans({ background: cachedPlans !== null })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const rawPlanId = params.get('plan_id') ?? params.get('plan')
    const planId = rawPlanId ? Number(rawPlanId) : null
    if (planId === null || !Number.isFinite(planId) || planId <= 0) return

    if (selectedPlanId !== planId) {
      setSelectedPlanId(planId)
      setPlanLoadError(null)
    }
    if (selectedPlanId !== planId) {
      planDetailFetchRef.current = null
    }
    if (Number(planDetail?.id) === planId) {
      planDetailFetchRef.current = planId
      return
    }
    if (planDetailFetchRef.current === planId || loadingDetail) return
    if (planLoadError) return
    planDetailFetchRef.current = planId
    void loadPlanDetailRef.current?.(planId)
  }, [loadingDetail, planDetail?.id, planLoadError, selectedPlanId, apiBase, accessToken])
  // loadPlanDetail via ref — avoid effect loop on callback identity

  useEffect(() => () => {
    planLoadAbortRef.current?.abort()
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await staffApi.getUsers(bootstrap, session)
        if (cancelled) return
        const filtered = (res.users ?? [])
          .filter((u) => /doctor|врач/i.test(u.role_key) || /доктор|врач/i.test(u.role_label))
          .map((u) => ({ id: u.id, display_name: u.display_name, role_label: u.role_label }))
        setDoctors(filtered.length > 0 ? filtered : (res.users ?? []).map((u) => ({ id: u.id, display_name: u.display_name, role_label: u.role_label })))
      } catch {
        setDoctors([])
      }
    })()
    return () => { cancelled = true }
  }, [bootstrap, session])

  useEffect(() => {
    const patientId = planDetail?.patient_id ?? null
    if (!patientId || patientId <= 0) {
      misSummariesPatientRef.current = null
      return
    }
    if (misSummariesPatientRef.current === patientId) return
    misSummariesPatientRef.current = patientId
    void loadMisImportedSummaries(patientId)
  }, [planDetail?.patient_id, loadMisImportedSummaries])

  useEffect(() => {
    for (const planId of expandedMisPlanIds) {
      if (!misServicesCache[planId] && !misServicesLoadingIds.includes(planId)) {
        void loadMisPlanServices(planId)
      }
    }
  }, [expandedMisPlanIds, misServicesCache, misServicesLoadingIds])

  // ---- Handlers ----

  async function handleSelectPlan(planId: number) {
    setSelectedPlanId(planId)
    const url = new URL(window.location.href)
    url.searchParams.set('plan_id', String(planId))
    window.history.replaceState(null, '', url)
    await loadPlanDetail(planId)
  }

  function handleClosePlan() {
    setSelectedPlanId(null)
    const url = new URL(window.location.href)
    url.searchParams.delete('plan_id')
    url.searchParams.delete('plan')
    window.history.replaceState(null, '', url)
    setPlanDetail(null)
    setPlanLoadError(null)
    setStageOrder([])
    setExpandedMisPlanIds([])
    setMisServicesCache({})
    setMisServicesLoadingIds([])
    setSelectedMisIds(new Set())
    misSummariesPatientRef.current = null
    setMisImportedSummaries([])
    setMisPatientId(null)
  }

  async function loadMisPlanServices(planId: string, force = false) {
    if (!force && misServicesCache[planId]) return
    setMisServicesLoadingIds((prev) => prev.includes(planId) ? prev : [...prev, planId])
    const misAbort = new AbortController()
    const misTimeoutId = window.setTimeout(() => misAbort.abort(), MIS_SUMMARIES_TIMEOUT_MS)
    try {
      const res = await fetch(`${apiBase}/staff/mis/treatment-plans/${encodeURIComponent(planId)}`, {
        headers: { Authorization: `Bearer ${session.accessToken}` },
        signal: misAbort.signal,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as { plan?: { plan_id: string; stages?: unknown[] } }
      const selected = data.plan
        ? misImportedServicesFromPlan(data.plan as Parameters<typeof misImportedServicesFromPlan>[0])
        : []
      const services = misImportedServicesToMisServiceItems(planId, selected)
      setMisServicesCache((prev) => ({ ...prev, [planId]: services }))
      logEvent(bootstrap.apiBase, session.accessToken, EventTypes.MIS_DATA_FETCHED, {
        mis_plan_id: planId,
        service_count: services.length,
      })
    } catch {
      setMisServicesCache((prev) => ({ ...prev, [planId]: [] }))
    } finally {
      window.clearTimeout(misTimeoutId)
      setMisServicesLoadingIds((prev) => prev.filter((id) => id !== planId))
    }
  }

  async function handleReloadDetail() {
    if (selectedPlanId) {
      await Promise.all([loadPlanDetail(selectedPlanId), loadPlans()])
    }
  }

  const handleMisPlansRefresh = useCallback((misPlanId?: string | null) => {
    const patientId = planDetail?.patient_id ?? null
    if (!patientId) return
    if (misPlanId) {
      setMisServicesCache((prev) => {
        const next = { ...prev }
        delete next[misPlanId]
        return next
      })
    }
    void loadMisImportedSummaries(patientId, misPlanId ?? undefined).then(() => {
      if (misPlanId) void loadMisPlanServices(misPlanId, true)
    })
  }, [planDetail?.patient_id, loadMisImportedSummaries])

  function handleStageDragStart(event: DragStartEvent) {
    const data = event.active.data.current as { kind?: string; items?: MisDragItem[] } | undefined
    if (data?.kind === 'mis-service' && Array.isArray(data.items)) {
      setDragPreview(data.items)
    } else {
      setDragPreview([])
    }
  }

  async function handleStageDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setDragPreview([])
    if (!over || !planDetail) return

    const activeData = active.data.current as { kind?: string; items?: MisDragItem[] } | undefined
    const overId = String(over.id)

    if (activeData?.kind === 'mis-service') {
      const stageId = overId.startsWith('stage-drop:')
        ? Number(overId.replace('stage-drop:', ''))
        : Number(overId)
      if (!Number.isFinite(stageId) || stageId <= 0 || !stageOrder.includes(stageId)) return

      const items = activeData.items ?? []
      if (items.length === 0) return

      let added = 0
      const errors: string[] = []
      const sourceDoctorName = items.map((item) => item.service.source_doctor_name).find((name): name is string => Boolean(name?.trim()))

      for (let i = 0; i < items.length; i++) {
        const svc = items[i].service
        if (!svc?.service_name) continue
        try {
          const quantity = svc.quantity ?? 1
          const effectiveTotal = svc.total_price ?? svc.unit_price ?? 0
          const effectiveUnitPrice = quantity > 0 ? effectiveTotal / quantity : effectiveTotal

          await staffApi.addService(bootstrap, session, planDetail.id, stageId, {
            name: svc.service_name,
            tooth: svc.tooth_number ?? '',
            qty: quantity,
            price: effectiveUnitPrice,
            total_price: effectiveTotal,
            original_unit_price: svc.unit_price ?? null,
            sort_order: 1000 + i,
            mis_source_plan_id: items[i].planId ?? undefined,
            mis_source_plan_uuid: items[i].misPlanUuid ?? items[i].service.mis_plan_uuid ?? undefined,
          })
          logEvent(bootstrap.apiBase, session.accessToken, EventTypes.MIS_SERVICE_DRAGGED_TO_PLAN, {
            mis_plan_id: items[i].planId ?? null,
            service_name: svc.service_name,
            tooth_number: svc.tooth_number ?? null,
            quantity: svc.quantity ?? 1,
            plan_id: planDetail.id,
            stage_id: stageId,
          })
          added++
        } catch (err) {
          errors.push(err instanceof Error ? err.message : String(err))
        }
      }

      if (added > 0) {
        const targetStage = (planDetail.stages ?? []).find((stage) => stage.id === stageId)
        const matchedDoctor = sourceDoctorName
          ? doctors.find((doctor) => normalizeDoctorName(doctor.display_name) === normalizeDoctorName(sourceDoctorName))
          : null
        if (!planDetail.doctor_id && !planDetail.doctor_name && sourceDoctorName) {
          try {
            await staffApi.updateConstructorPlan(bootstrap, session, planDetail.id, {
              doctor_id: matchedDoctor?.id ?? null,
              doctor_name: matchedDoctor?.display_name ?? sourceDoctorName,
            })
          } catch (err) {
            showToast('error', `Услуги перенесены, но лечащий врач не сохранён: ${err instanceof Error ? err.message : String(err)}`)
          }
        }
        if (targetStage && !targetStage.doctor_id && !targetStage.doctor_name && sourceDoctorName) {
          try {
            await staffApi.updateStage(bootstrap, session, planDetail.id, stageId, {
              doctor_id: matchedDoctor?.id ?? null,
              doctor_name: matchedDoctor?.display_name ?? sourceDoctorName,
            })
          } catch (err) {
            showToast('error', `Услуги перенесены, но врач этапа не сохранён: ${err instanceof Error ? err.message : String(err)}`)
          }
        }
        await handleReloadDetail()
        if (items.length > 1) {
          showToast('success', `Перенесено услуг: ${added} из ${items.length}`)
          setSelectedMisIds(new Set())
        }
      }
      if (errors.length > 0) {
        showToast('error', `Ошибка добавления услуги: ${errors[0]}`)
      }
      return
    }

    if (active.id === over.id) return

    const oldIdx = stageOrder.indexOf(active.id as number)
    const newIdx = stageOrder.indexOf(over.id as number)
    if (oldIdx < 0 || newIdx < 0) return
    const newOrder = arrayMove(stageOrder, oldIdx, newIdx)
    setStageOrder(newOrder)
    try {
      await staffApi.reorderStages(bootstrap, session, planDetail.id, newOrder)
    } catch (err) {
      showToast('error', `Не удалось переупорядочить этапы: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  async function handleAddStage() {
    if (!planDetail) return
    const title = prompt('Название нового этапа:')
    if (!title?.trim()) return
    try {
      await staffApi.addStage(bootstrap, session, planDetail.id, { title: title.trim(), sort_order: stageOrder.length })
      await handleReloadDetail()
    } catch {
      // ignore
    }
  }

  async function handleStatusChange(status: string) {
    if (!planDetail) return
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, planDetail.id, { status })
      await handleReloadDetail()
    } catch {
      // ignore
    }
  }

  async function handlePlanDoctorChange(value: string) {
    if (!planDetail) return
    const doctorId = value === '' ? null : Number(value)
    const doctorName = doctorId === null
      ? null
      : (doctors.find((doctor) => doctor.id === doctorId)?.display_name ?? null)
    try {
      await staffApi.updateConstructorPlan(bootstrap, session, planDetail.id, { doctor_id: doctorId, doctor_name: doctorName })
      await handleReloadDetail()
    } catch (err) {
      showToast('error', `Не удалось сохранить лечащего врача: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // ---- Derived values ----

  const activePatientId = planDetail?.patient_id ?? null
  const activePatientName = (planDetail?.patient_name ?? '').trim()

  const filteredPlans = plans
    .filter((p) => {
      if (activePatientId && p.patient_id !== activePatientId) return false
      if (!search) return true
      const q = search.toLowerCase()
      return (
        (p.title ?? '').toLowerCase().includes(q) ||
        (p.patient_name ?? '').toLowerCase().includes(q) ||
        (p.doctor_name ?? '').toLowerCase().includes(q)
      )
    })
    .slice()
    .sort(comparePlansByCreatedAtDesc)

  const stages = planDetail
    ? (planDetail.stages ?? []).slice().sort((a, b) => getStageSortOrder(a) - getStageSortOrder(b))
    : []

  const totalAmount = stages.flatMap(getServices).reduce((sum, svc) => {
    const unitPrice = svc.unit_price ?? svc.total_price ?? svc.price ?? 0
    return sum + unitPrice * getServiceQty(svc)
  }, 0)

  // ---- Render ----

  return (
    <ConstructorWorkflowProvider>
      <ConstructorDiagnosticsDraftProvider>
      <CloseWorkflowWindowsOnPlanChange planId={selectedPlanId} />
    <StaffShell
      navigation={navigation}
      currentView="constructor"
      onNavigate={onNavigate}
      session={session}
      bootstrapTenant={bootstrap.tenant}
      onLogout={onLogout}
      fullBleed
    >
      <style>{constructorPrintStyle}</style>

      <DndContext
        sensors={stageSensors}
        collisionDetection={closestCenter}
        onDragStart={handleStageDragStart}
        onDragEnd={handleStageDragEnd}
      >
        <ResizableGroup className="no-print flex min-h-0 flex-1 overflow-hidden" orientation="horizontal">

          {/* Left rail */}
          {!planDetail ? (
            <ConstructorPlanListRail
              panelId="left-rail-no-plan"
              defaultSize="28%"
              minSize="18%"
              maxSize="55%"
              plans={filteredPlans}
              loading={loading}
              selectedPlanId={selectedPlanId}
              search={search}
              activePatientId={null}
              activePatientName=""
              onSelectPlan={(planId) => void handleSelectPlan(planId)}
              onCreatePlan={() => setShowCreate(true)}
              onSearchChange={setSearch}
            />
          ) : (
            <ResizablePanel
              id="left-rail-plan"
              defaultSize="26%"
              minSize="16%"
              maxSize="55%"
              className="flex min-h-0 flex-col border-r border-slate-300 bg-white"
            >
              {activePatientId !== null && (
                <button
                  type="button"
                  onClick={handleClosePlan}
                  className="flex shrink-0 items-center gap-1.5 border-b border-slate-200 bg-slate-100 px-2.5 py-1.5 text-left text-[11px] font-medium text-slate-700 hover:bg-slate-200"
                  title="Закрыть план и вернуться ко всем планам конструктора"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="flex-1 truncate">К общему списку планов</span>
                </button>
              )}

              <ResizableGroup className="flex min-h-0 flex-1 flex-col" orientation="vertical">
                <ConstructorPlanListRail
                  panelId="left-rail-constructor"
                  defaultSize="38%"
                  minSize="6%"
                  collapsible
                  plans={filteredPlans}
                  loading={loading}
                  selectedPlanId={selectedPlanId}
                  search={search}
                  activePatientId={activePatientId}
                  activePatientName={activePatientName}
                  onSelectPlan={(planId) => void handleSelectPlan(planId)}
                  onCreatePlan={() => setShowCreate(true)}
                  onSearchChange={setSearch}
                />

                <HResizeHandle />

                <MisPlansSidebar
                  bootstrap={bootstrap}
                  session={session}
                  misPlans={misImportedSummaries}
                  misLoading={misLoading}
                  misSearch={misSearch}
                  expandedMisPlanIds={expandedMisPlanIds}
                  misServicesCache={misServicesCache}
                  misServicesLoadingIds={misServicesLoadingIds}
                  selectedMisIds={selectedMisIds}
                  allSelectedItems={allSelectedItems}
                  usedMisServiceKeys={usedMisServiceKeys}
                  patientName={planDetail?.patient_name ?? ''}
                  patientId={planDetail?.patient_id}
                  onMisSearchChange={setMisSearch}
                  onToggleMisPlan={(planId) => {
                    setExpandedMisPlanIds((prev) =>
                      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId],
                    )
                  }}
                  onToggleMisSelected={toggleMisSelected}
                  onClearMisSelection={clearMisSelection}
                  onRefresh={() => { void loadMisImportedSummaries(planDetail?.patient_id ?? null) }}
                  onLoadMisPlanServices={(planId) => void loadMisPlanServices(planId)}
                  onCorrectPlan={setMisPlanCorrectionTarget}
                />
              </ResizableGroup>
            </ResizablePanel>
          )}

          <VResizeHandle />

          {/* Center column */}
          <ResizablePanel
            id="center"
            defaultSize={planDetail ? (drawerOpen ? '52%' : '72%') : '72%'}
            minSize="30%"
            className="flex min-h-0 flex-col"
          >
            {loadingDetail ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
                <p className="text-sm text-slate-400">Загрузка плана #{selectedPlanId ?? '…'}...</p>
                <p className="text-xs text-slate-400">Если загрузка длится более 45 с, появится сообщение об ошибке.</p>
              </div>
            ) : planLoadError ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-rose-50/40 px-6 py-10 text-center">
                <div className="max-w-md rounded-lg border border-rose-200 bg-white px-5 py-4 shadow-sm">
                  <h2 className="text-base font-semibold text-rose-800">Не удалось открыть план</h2>
                  <p className="mt-2 text-sm text-rose-700">{planLoadError}</p>
                  {selectedPlanId ? (
                    <button
                      type="button"
                      onClick={() => void loadPlanDetail(selectedPlanId)}
                      className="mt-4 inline-flex items-center rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
                    >
                      Повторить загрузку
                    </button>
                  ) : null}
                </div>
              </div>
            ) : !planDetail ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 bg-slate-50 px-6 py-10 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-500">
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6M9 8h6M5 6a2 2 0 012-2h10a2 2 0 012 2v14l-4-2-3 2-3-2-4 2V6z" />
                  </svg>
                </div>
                <div className="max-w-md">
                  <h2 className="text-base font-semibold text-slate-800">Выберите план в списке слева</h2>
                  <p className="mt-1.5 text-sm text-slate-500">
                    Чтобы открыть план — кликните по карточке слева. Чтобы создать новый — нажмите «+ Создать план» вверху.
                  </p>
                  <p className="mt-3 text-xs text-slate-400">
                    Всего планов конструктора: <span className="font-semibold text-slate-600">{filteredPlans.length}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCreate(true)}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Создать план
                </button>
              </div>
            ) : (
              <>
                <ConstructorPlanHeader
                  planDetail={planDetail}
                  doctors={doctors}
                  totalAmount={totalAmount}
                  drawerOpen={drawerOpen}
                  onClose={handleClosePlan}
                  onPrint={() => window.print()}
                  onDrawerToggle={() => setDrawerOpen((v) => !v)}
                  onStatusChange={(status) => void handleStatusChange(status)}
                  onDoctorChange={(value) => void handlePlanDoctorChange(value)}
                />
                <ConstructorStagesEditor
                  planDetail={planDetail}
                  stages={stages}
                  stageOrder={stageOrder}
                  totalAmount={totalAmount}
                  bootstrap={bootstrap}
                  session={session}
                  doctors={doctors}
                  onReload={() => void handleReloadDetail()}
                  onAddStage={() => void handleAddStage()}
                />
              </>
            )}
          </ResizablePanel>

          {/* Right drawer */}
          {planDetail && drawerOpen ? (
            <>
              <VResizeHandle />
              <ConstructorRightDrawer
                planDetail={planDetail}
                stages={stages}
                totalAmount={totalAmount}
                bootstrap={bootstrap}
                session={session}
                lkPatientId={planDetail.patient_id ?? null}
                misPatientId={misPatientId}
                staffUserId={
                  session.user?.id != null && String(session.user.id).trim() !== ''
                    ? String(session.user.id)
                    : ''
                }
                onReload={() => void handleReloadDetail()}
                onMisPlansRefresh={(misPlanId) => {
                  handleMisPlansRefresh(misPlanId)
                  void handleReloadDetail()
                }}
                onClose={() => setDrawerOpen(false)}
              />
            </>
          ) : null}
        </ResizableGroup>

        <DragOverlay dropAnimation={null}>
          {dragPreview.length > 0 ? <DragOverlayCard items={dragPreview} /> : null}
        </DragOverlay>
      </DndContext>

      {/* Toast */}
      {toast ? (
        <div
          role="status"
          className={`pointer-events-none fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 rounded-md px-4 py-2 text-sm font-medium shadow-lg ${
            toast.kind === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.text}
        </div>
      ) : null}

      {/* Print view */}
      {planDetail && (
        <ConstructorPrintView
          planDetail={planDetail}
          stages={stages}
          totalAmount={totalAmount}
        />
      )}

      {misPlanCorrectionTarget && misPatientId ? (
        <MisPlanCorrectionShell
          apiBase={bootstrap.apiBase}
          session={session}
          misPatientId={misPatientId}
          staffUserId={String(session.user.id)}
          planSummary={misPlanCorrectionTarget}
          onClose={() => setMisPlanCorrectionTarget(null)}
          onApplied={(misPlanId) => {
            handleMisPlansRefresh(misPlanId)
            setMisPlanCorrectionTarget(null)
            showToast('success', misPlanId ? 'Скорректированный план применён в МИС' : 'План применён')
          }}
        />
      ) : null}

      {showCreate && (
        <CreatePlanModal
          bootstrap={bootstrap}
          session={session}
          onCreated={(planId) => {
            setShowCreate(false)
            void loadPlans().then(() => void handleSelectPlan(planId))
          }}
          onClose={() => setShowCreate(false)}
        />
      )}
    </StaffShell>
      {planDetail ? (
        <ConstructorWorkflowWindows
          planDetail={planDetail}
          stages={stages}
          totalAmount={totalAmount}
          bootstrap={bootstrap}
          session={session}
          patientName={planDetail.patient_name ?? ''}
          misPatientId={misPatientId}
          misImportedSummaries={misImportedSummaries}
          onReload={() => void loadPlans()}
        />
      ) : null}
      </ConstructorDiagnosticsDraftProvider>
    </ConstructorWorkflowProvider>
  )
}
